import {
  StateField,
  StateEffect,
  type Extension,
  type TransactionSpec,
  Prec,
  Facet,
} from "@codemirror/state";
import {
  EditorView,
  Decoration,
  type DecorationSet,
  WidgetType,
  keymap,
  ViewPlugin,
  type ViewUpdate,
} from "@codemirror/view";
import { api } from "../../lib/api";

// ── Configuration facet ──

export interface GhostTextConfig {
  enabled: boolean;
  debounceMs: number;
  minPrefixLength: number;
  language: "latex" | "markdown" | "docx";
  maxTokens: number;
}

const defaultConfig: GhostTextConfig = {
  enabled: true,
  debounceMs: 800,
  minPrefixLength: 3,
  language: "latex",
  maxTokens: 256,
};

export const ghostTextConfig = Facet.define<GhostTextConfig, GhostTextConfig>({
  combine(values) {
    return values.length ? values[values.length - 1] : defaultConfig;
  },
});

// ── State effects ──

const setGhostText = StateEffect.define<{ pos: number; text: string } | null>();
const setGhostCandidates = StateEffect.define<{
  pos: number;
  candidates: string[];
  index: number;
} | null>();

// ── Widgets ──

class GhostTextWidget extends WidgetType {
  text: string;
  constructor(text: string) {
    super();
    this.text = text;
  }

  toDOM(): HTMLElement {
    const container = document.createElement("span");
    container.className = "cm-ghost-text-container";

    const lines = this.text.split("\n");
    for (let i = 0; i < lines.length; i++) {
      if (i > 0) {
        container.appendChild(document.createElement("br"));
      }
      const span = document.createElement("span");
      span.className = "cm-ghost-text";
      span.dataset.line = String(i);
      span.textContent = lines[i];
      container.appendChild(span);
    }

    return container;
  }

  eq(other: GhostTextWidget) {
    return this.text === other.text;
  }

  ignoreEvent() {
    return true;
  }
}

class CandidateIndicatorWidget extends WidgetType {
  index: number;
  total: number;
  constructor(index: number, total: number) {
    super();
    this.index = index;
    this.total = total;
  }

  toDOM(): HTMLElement {
    const pill = document.createElement("span");
    pill.className = "cm-ghost-indicator";
    pill.textContent = `${this.index + 1}/${this.total}`;
    return pill;
  }

  eq(other: CandidateIndicatorWidget) {
    return this.index === other.index && this.total === other.total;
  }

  ignoreEvent() {
    return true;
  }
}

// ── State field ──

interface GhostState {
  pos: number;
  text: string;
  candidates: string[];
  candidateIndex: number;
  decorations: DecorationSet;
}

function buildDecorations(pos: number, text: string, candidates: string[], index: number): DecorationSet {
  const decos = [
    Decoration.widget({ widget: new GhostTextWidget(text), side: 1 }).range(pos),
  ];

  if (candidates.length > 1) {
    decos.push(
      Decoration.widget({ widget: new CandidateIndicatorWidget(index, candidates.length), side: 1 }).range(pos),
    );
  }

  return Decoration.set(decos);
}

const ghostField = StateField.define<GhostState | null>({
  create() {
    return null;
  },

  update(value, tr) {
    for (const e of tr.effects) {
      if (e.is(setGhostText)) {
        if (!e.value) return null;
        const { pos, text } = e.value;
        return {
          pos,
          text,
          candidates: [text],
          candidateIndex: 0,
          decorations: buildDecorations(pos, text, [text], 0),
        };
      }
      if (e.is(setGhostCandidates)) {
        if (!e.value) return null;
        const { pos, candidates, index } = e.value;
        const text = candidates[index] ?? "";
        return {
          pos,
          text,
          candidates,
          candidateIndex: index,
          decorations: buildDecorations(pos, text, candidates, index),
        };
      }
    }

    if (tr.docChanged || tr.selection) {
      return null;
    }

    return value;
  },

  provide(field) {
    return EditorView.decorations.from(field, (v) => v?.decorations ?? Decoration.none);
  },
});

// ── Accept / dismiss / cycle helpers ──

function acceptGhostFull(view: EditorView): boolean {
  const ghost = view.state.field(ghostField);
  if (!ghost) return false;

  const spec: TransactionSpec = {
    changes: { from: ghost.pos, insert: ghost.text },
    selection: { anchor: ghost.pos + ghost.text.length },
    effects: setGhostText.of(null),
  };
  view.dispatch(spec);
  return true;
}

function acceptGhostWord(view: EditorView): boolean {
  const ghost = view.state.field(ghostField);
  if (!ghost) return false;

  const match = ghost.text.match(/^(\S+\s?|\s+)/);
  if (!match) return acceptGhostFull(view);

  const word = match[0];
  const remaining = ghost.text.slice(word.length);

  const changes = { from: ghost.pos, insert: word };
  const newPos = ghost.pos + word.length;

  if (remaining) {
    view.dispatch({
      changes,
      selection: { anchor: newPos },
      effects: setGhostText.of({ pos: newPos, text: remaining }),
    });
  } else {
    view.dispatch({
      changes,
      selection: { anchor: newPos },
      effects: setGhostText.of(null),
    });
  }

  return true;
}

function acceptGhostLine(view: EditorView): boolean {
  const ghost = view.state.field(ghostField);
  if (!ghost) return false;

  const nlIndex = ghost.text.indexOf("\n");
  if (nlIndex === -1) return acceptGhostFull(view);

  const line = ghost.text.slice(0, nlIndex + 1);
  const remaining = ghost.text.slice(nlIndex + 1);

  const changes = { from: ghost.pos, insert: line };
  const newPos = ghost.pos + line.length;

  if (remaining) {
    view.dispatch({
      changes,
      selection: { anchor: newPos },
      effects: setGhostText.of({ pos: newPos, text: remaining }),
    });
  } else {
    view.dispatch({
      changes,
      selection: { anchor: newPos },
      effects: setGhostText.of(null),
    });
  }

  return true;
}

function cycleGhostNext(view: EditorView): boolean {
  const ghost = view.state.field(ghostField);
  if (!ghost || ghost.candidates.length <= 1) return false;

  const nextIndex = (ghost.candidateIndex + 1) % ghost.candidates.length;
  view.dispatch({
    effects: setGhostCandidates.of({
      pos: ghost.pos,
      candidates: ghost.candidates,
      index: nextIndex,
    }),
  });
  return true;
}

function cycleGhostPrev(view: EditorView): boolean {
  const ghost = view.state.field(ghostField);
  if (!ghost || ghost.candidates.length <= 1) return false;

  const prevIndex = (ghost.candidateIndex - 1 + ghost.candidates.length) % ghost.candidates.length;
  view.dispatch({
    effects: setGhostCandidates.of({
      pos: ghost.pos,
      candidates: ghost.candidates,
      index: prevIndex,
    }),
  });
  return true;
}

function dismissGhost(view: EditorView): boolean {
  const ghost = view.state.field(ghostField);
  if (!ghost) return false;
  view.dispatch({ effects: setGhostText.of(null) });
  return true;
}

// ── Fetch status (observable for UI) ──

export type GhostTextStatus = "idle" | "fetching" | "error";

type StatusListener = (status: GhostTextStatus, errorMsg?: string) => void;
const _statusListeners = new Set<StatusListener>();
let _currentStatus: GhostTextStatus = "idle";
let _lastError: string | undefined;
let _consecutiveErrors = 0;

function setFetchStatus(status: GhostTextStatus, errorMsg?: string) {
  _currentStatus = status;
  _lastError = errorMsg;
  if (status === "error") {
    _consecutiveErrors++;
  } else if (status === "idle") {
    _consecutiveErrors = 0;
  }
  _statusListeners.forEach((fn) => fn(status, errorMsg));
}

export function onGhostTextStatus(fn: StatusListener): () => void {
  _statusListeners.add(fn);
  fn(_currentStatus, _lastError);
  return () => { _statusListeners.delete(fn); };
}

export function getGhostTextStatus() {
  return { status: _currentStatus, error: _lastError, consecutiveErrors: _consecutiveErrors };
}

// ── Fetch plugin ──

const MAX_CANDIDATES = 1;
const MAX_BACKOFF_ERRORS = 5;

class GhostTextPlugin {
  private timer: ReturnType<typeof setTimeout> | null = null;
  private controller: AbortController | null = null;

  update(update: ViewUpdate) {
    if (!update.docChanged) return;

    const config = update.view.state.facet(ghostTextConfig);
    if (!config.enabled) return;

    if (_consecutiveErrors >= MAX_BACKOFF_ERRORS) return;

    this.cancel();

    const { state } = update.view;
    const pos = state.selection.main.head;
    const prefix = state.sliceDoc(0, pos);
    if (prefix.length < config.minPrefixLength) return;

    this.timer = setTimeout(() => {
      this.fetchCompletions(update.view, pos, prefix, state.sliceDoc(pos));
    }, config.debounceMs);
  }

  destroy() {
    this.cancel();
  }

  private cancel() {
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
    if (this.controller) {
      this.controller.abort();
      this.controller = null;
    }
  }

  private async fetchCompletions(view: EditorView, pos: number, prefix: string, suffix: string) {
    const controller = new AbortController();
    this.controller = controller;
    const config = view.state.facet(ghostTextConfig);

    setFetchStatus("fetching");

    try {
      const candidates: string[] = [];

      const fetchers = Array.from({ length: MAX_CANDIDATES }, () =>
        this.fetchOne(prefix, suffix, config, controller.signal),
      );

      const results = await Promise.allSettled(fetchers);

      if (controller.signal.aborted) return;

      let fetchError: string | undefined;
      for (const r of results) {
        if (r.status === "fulfilled" && r.value) {
          candidates.push(r.value);
        } else if (r.status === "rejected") {
          fetchError = r.reason instanceof Error ? r.reason.message : String(r.reason);
        }
      }

      if (candidates.length === 0) {
        if (fetchError) {
          setFetchStatus("error", fetchError);
        } else {
          setFetchStatus("idle");
        }
        return;
      }

      setFetchStatus("idle");

      const currentPos = view.state.selection.main.head;
      if (currentPos !== pos) return;

      const unique = [...new Set(candidates)];

      if (unique.length === 1) {
        view.dispatch({ effects: setGhostText.of({ pos, text: unique[0] }) });
      } else {
        view.dispatch({
          effects: setGhostCandidates.of({ pos, candidates: unique, index: 0 }),
        });
      }
    } catch (err) {
      if (controller.signal.aborted) return;
      const msg = err instanceof Error ? err.message : "Autocomplete failed";
      setFetchStatus("error", msg);
    }
  }

  private async fetchOne(
    prefix: string,
    suffix: string,
    config: GhostTextConfig,
    signal: AbortSignal,
  ): Promise<string | null> {
    const { completionProvider, completionModel } = (await import("../../stores/ai")).useAIStore.getState();
    let completion = "";
    for await (const chunk of api.stream(
      "/api/v1/ai/complete",
      {
        prefix,
        suffix,
        language: config.language,
        max_tokens: config.maxTokens,
        provider: completionProvider || undefined,
        model: completionModel || undefined,
      },
      signal,
    )) {
      try {
        const data = JSON.parse(chunk);
        if (data.text) completion += data.text;
      } catch {
        completion += chunk;
      }
    }
    const trimmed = completion.trim();
    return trimmed || null;
  }
}

// ── Theme ──

const ghostTheme = EditorView.theme({
  ".cm-ghost-text-container": {
    display: "inline",
    pointerEvents: "none",
    userSelect: "none",
  },
  ".cm-ghost-text": {
    color: "var(--text-muted)",
    fontStyle: "italic",
    pointerEvents: "none",
    userSelect: "none",
  },
  '.cm-ghost-text[data-line="0"]': {
    opacity: "0.55",
  },
  '.cm-ghost-text[data-line="1"]': {
    opacity: "0.4",
  },
  '.cm-ghost-text[data-line="2"]': {
    opacity: "0.28",
  },
  '.cm-ghost-text[data-line="3"]': {
    opacity: "0.18",
  },
  ".cm-ghost-indicator": {
    display: "inline-block",
    marginLeft: "6px",
    padding: "1px 5px",
    fontSize: "9px",
    fontFamily: "var(--font-mono)",
    fontStyle: "normal",
    color: "var(--text-muted)",
    background: "var(--bg-surface-hover)",
    border: "1px solid var(--border-hairline)",
    borderRadius: "var(--radius-sm)",
    opacity: "0.7",
    verticalAlign: "middle",
    pointerEvents: "none",
    userSelect: "none",
  },
});

// ── Export ──

export function ghostText(config?: Partial<GhostTextConfig>): Extension {
  const merged = { ...defaultConfig, ...config };

  return [
    ghostTextConfig.of(merged),
    ghostField,
    ViewPlugin.fromClass(GhostTextPlugin),
    Prec.highest(
      keymap.of([
        {
          key: "Tab",
          run: acceptGhostFull,
        },
        {
          key: "Mod-ArrowRight",
          run: acceptGhostWord,
        },
        {
          key: "Shift-Tab",
          run: acceptGhostLine,
        },
        {
          key: "Alt-]",
          run: cycleGhostNext,
        },
        {
          key: "Alt-[",
          run: cycleGhostPrev,
        },
        {
          key: "Escape",
          run: dismissGhost,
        },
      ]),
    ),
    ghostTheme,
  ];
}
